/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import fixtures from './spreadsheets.fixtures.json';
import type {
  ImportCommitResult,
  ImportDetail,
  ImportPreview,
  ImportSession,
  ImportSettings,
  PreviewCounts,
  SpreadsheetEntity,
  SpreadsheetHubData,
} from '../types';

/**
 * The fixture-backed half of the spreadsheet API, used while
 * `FF_MOCK_SPREADSHEET_API` is on. It keeps one session in memory so the
 * Conferir screen behaves like the real thing: a chip change answers with a
 * recomputed preview, and a refresh with `?session=` in the URL finds the
 * session still there.
 */

type MockSession = {
  session: ImportSession;
  settings: ImportSettings;
};

const sessions = new Map<string, MockSession>();

/** Simulates a round trip so loading states actually render. */
const delay = (ms: number) => new Promise<void>((resolve) => { setTimeout(resolve, ms); });

/** The fixture preview for an entity, deep-copied so callers can mutate it. */
function basePreview(entity: SpreadsheetEntity): ImportPreview {
  return JSON.parse(JSON.stringify(fixtures.previews[entity])) as ImportPreview;
}

/** The fixture session for an entity, deep-copied. */
function baseSession(entity: SpreadsheetEntity): ImportSession {
  return JSON.parse(JSON.stringify(fixtures.sessions[entity])) as ImportSession;
}

/**
 * Opens a mock session from a picked file.
 *
 * The entity is "detected" from the file name, which is what the real
 * endpoint promises to do from the header row.
 *
 * @param input - The picked file.
 * @returns The session the Conferir screen is built from.
 */
export async function mockCreateSession(
  input: { file: File },
): Promise<ImportSession> {
  await delay(700);
  const name = input.file?.name ?? '';
  const looksLikeProperties = /imov|imóv|carteira|propert/i.test(name);
  const entity: SpreadsheetEntity = looksLikeProperties ? 'properties' : 'clients';
  const session = baseSession(entity);
  if (name) session.file_name = name;
  sessions.set(session.session_id, { session, settings: { entity } });
  return session;
}

/**
 * Re-opens a session by id, so a refresh on the Conferir screen keeps working.
 *
 * @param sessionId - The id carried in `?session=`.
 * @returns The session, or `null` when this tab never opened it.
 */
export function mockGetSession(sessionId: string): ImportSession | null {
  return sessions.get(sessionId)?.session ?? null;
}

/** Applies the stored mapping overrides on top of the fixture columns. */
function applyMapping(preview: ImportPreview, settings: ImportSettings): void {
  const mapping = settings.mapping;
  if (!mapping) return;
  for (const column of preview.columns) {
    if (Object.prototype.hasOwnProperty.call(mapping, column.index)) {
      column.field = mapping[column.index];
      column.suggested = false;
    }
  }
}

/**
 * Scales the fixture's status counts to the rows actually present.
 *
 * The fixture ships ten sample rows but claims a 48-row sheet, which is what
 * the real endpoint does too: the counts cover the whole file, not the page.
 */
function countsFor(preview: ImportPreview): PreviewCounts {
  return preview.counts;
}

/**
 * Returns one page of the preview, narrowed to a status when asked.
 *
 * @param sessionId - The open session.
 * @param params - Status filter and pagination window.
 * @returns The preview page, with counts covering the whole sheet.
 */
export async function mockGetPreview(
  sessionId: string,
  params: { status?: string; offset?: number; limit?: number } = {},
): Promise<ImportPreview> {
  await delay(250);
  const entry = sessions.get(sessionId);
  const entity = entry?.settings.entity ?? 'clients';
  const preview = basePreview(entity);
  if (entry) applyMapping(preview, entry.settings);
  const filtered = params.status && params.status !== 'all'
    ? preview.rows.filter((row) => row.status === params.status)
    : preview.rows;
  const offset = params.offset ?? 0;
  const limit = params.limit ?? 50;
  return {
    ...preview,
    counts: countsFor(preview),
    rows: filtered.slice(offset, offset + limit),
  };
}

/**
 * Merges settings into the session and answers with the updated preview,
 * exactly as the PATCH endpoint is specified to.
 *
 * @param sessionId - The open session.
 * @param patch - The settings that changed.
 * @param status - The status filter currently active, so the answer matches the view.
 * @returns The recomputed preview.
 */
export async function mockPatchSession(
  sessionId: string,
  patch: ImportSettings,
  status?: string,
): Promise<ImportPreview> {
  const entry = sessions.get(sessionId);
  if (entry) {
    entry.settings = {
      ...entry.settings,
      ...patch,
      mapping: { ...entry.settings.mapping, ...patch.mapping },
    };
    // Switching entity re-detects the whole sheet, so the old column
    // overrides no longer address anything.
    if (patch.entity && patch.entity !== entry.session.entity_detected) {
      entry.settings.mapping = {};
      entry.session = { ...baseSession(patch.entity), session_id: sessionId };
    }
  }
  return mockGetPreview(sessionId, { status });
}

/**
 * Commits the import, deriving the created/merged split from the duplicate
 * rule the user chose.
 *
 * @param sessionId - The open session.
 * @returns The commit result the result banner is worded from.
 */
/**
 * How many polls the mock spends "processing" before it finishes.
 *
 * The real commit runs in a worker and answers `processing` with a cursor
 * for as long as it takes; a mock that jumps straight to `done` leaves the
 * progress cover unreachable in mock mode, which is exactly where it is
 * cheapest to look at.
 */
const MOCK_COMMIT_POLLS = 4;

/** Polls already answered per session, so the mock advances as it is asked. */
const commitPolls = new Map<string, number>();

/** The next `processing` answer, or `null` once the mock is finished. */
function nextCommitPoll(sessionId: string): ImportCommitResult | null {
  const polled = (commitPolls.get(sessionId) ?? 0) + 1;
  commitPolls.set(sessionId, polled);
  if (polled > MOCK_COMMIT_POLLS) {
    commitPolls.delete(sessionId);
    return null;
  }
  return {
    status: 'processing',
    session_id: sessionId,
    processed: (polled - 1) * 25,
    total: MOCK_COMMIT_POLLS * 25,
  };
}

/** The settled answer, once the mock has spent its processing polls. */
function settledCommit(sessionId: string): ImportCommitResult {
  const entry = sessions.get(sessionId);
  const entity = entry?.settings.entity ?? 'clients';
  const { counts } = basePreview(entity);
  const mode = entry?.settings.duplicate_mode ?? 'merge';
  const merged = mode === 'skip' ? 0 : counts.existing;
  const created = mode === 'create' ? counts.new + counts.existing : counts.new;
  return {
    import_id: `mock-import-${entity}`,
    status: 'done',
    created,
    merged: mode === 'create' ? 0 : merged,
    skipped: counts.invalid + (mode === 'skip' ? counts.existing : 0),
    invalid: counts.invalid,
  };
}

export async function mockCommit(sessionId: string): Promise<ImportCommitResult> {
  await delay(900);
  return nextCommitPoll(sessionId) ?? settledCommit(sessionId);
}

/** Pretends the undo succeeded; there is nothing persisted to roll back. */
export async function mockUndo(): Promise<void> {
  await delay(500);
}

/** The three hub sections, straight from the fixture. */
export async function mockImportDetail(importId: string): Promise<ImportDetail> {
  await delay(300);
  const entry = (fixtures.hub.history as unknown as ImportDetail[])
    .find((item) => item.import_id === importId);
  if (!entry) throw new Error(`Import not found: ${importId}`);
  const reason = entry.outcome === 'nothing_imported' ? 'already_exists' : 'rejected';
  return {
    ...entry,
    skipped_rows: Array.from({ length: entry.counts.skipped }, (_, i) => ({
      row_number: i + 2, reason,
    })),
    invalid_rows: Array.from({ length: entry.counts.invalid }, (_, i) => ({
      row_number: i + 40, reason: 'invalid' as const,
    })),
    rows_truncated: false,
  };
}

export async function mockHub(): Promise<SpreadsheetHubData> {
  await delay(400);
  return JSON.parse(JSON.stringify(fixtures.hub)) as SpreadsheetHubData;
}
